import { Button, HorizontalRule } from '@snsw/react-component-library';
import styles from './PaymentCallback.module.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ScrollToTop from '../../../shared/utils/scrollToTop';

import {
  Row,
  Col,
  SectionPage,
  ComponentLoader,
  IconDownload,
  IconNotificationSuccess,
} from '@snsw/react-component-library';
import { NextSteps } from '@onegov/react-component-library';
import { clearForm } from 'plantItemNew/redux/slices/formSlice';
import { cleanPdf, downloadSummaryPDF } from 'plantItemNew/redux/slices/pdfLinkSlice';
import { clearPlantItemLicenceTypes } from 'plantItemNew/redux/slices/plantItemLicenceTypesSlice';
import { validatePaymentCompleted } from 'shared/utils/Validation';

export const Confirmation = (props) => {
  ScrollToTop();

  const applicantDetails = useSelector((state) => state.applicantDetails);
  const paymentSuccess = useSelector((state) => state.paymentSuccess);
  const application = useSelector((state) => state.application);
  const orgDuration = useSelector((state) => state.orgDuration);
  const fees = useSelector((state) => state.fees);
  const pdfLink = useSelector((state) => state.pdfLink);

  const [emailSentTo, setEmailSentTo] = useState(sessionStorage['plant_emailAddress']);
  const [refNumber, setRefNumber] = useState(sessionStorage['plant_applicationNo']);
  const [applicationPaid, setApplicationPaid] = useState(sessionStorage['plant_item_paid']);

  const applicationNo = application ? application.applicationNo : null;
  const applicationId = application ? application.applicationId : null;
  const emailAddress = applicantDetails?.emailAddress;

  const { pdfData, pdfLoading, pdfFailed, pdfSucceeded } = pdfLink;

  const dispatch = useDispatch();

  const jsonSteps = [
    {
      title: 'Confirmation',
      description: 'You will receive your payment receipt and confirmation that your application has been lodged.',
    },
    {
      title: 'Additional contact',
      description: 'If any additional information is required to finalise your application, we will contact you.',
    },
    {
      title: 'Registration documents',
      description: 'Registration documents will be emailed to you and any nominated contact once finalised.',
    },
  ];

  const fetchSummaryPDF = () => {
    dispatch(downloadSummaryPDF(sessionStorage['plant_applicationId']));
  };

  useEffect(() => {
    const script = document.createElement('script');

    script.src = process.env.REACT_APP_QUICK_FEED;
    script.async = true;

    document.body.appendChild(script);

    window.onload = function () {
      window.quickFeed.init({
        placeholder: 'feedbackContainer',
        label: 'How was your online experience today?',
        environment: process.env.REACT_APP_QUICK_FEED_ENVIRONMENT,
        sourceApplication: process.env.REACT_APP_SOURCE_APPLICATION,
        commentForThumbsUp: true,
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const checkApplicationPaid = async (applicationId) => {
      const resp = await validatePaymentCompleted(applicationId);
      const paid = resp.data.isCompleted && resp.data.applicationId == applicationId;

      sessionStorage['plant_item_paid'] = paid;
      setApplicationPaid(paid);
    };

    if (applicationNo != null) {
      sessionStorage['plant_applicationNo'] = applicationNo;
      setRefNumber(applicationNo);
    }

    if (applicationId) {
      sessionStorage['plant_applicationId'] = applicationId;
      checkApplicationPaid(applicationId).catch(console.error);
    }

    if (emailAddress) {
      sessionStorage['plant_emailAddress'] = emailAddress;
      setEmailSentTo(emailAddress);
    }
  }, [applicationNo, applicationId, emailAddress]);

  useEffect(() => {
    if (pdfData && !pdfLoading && !pdfFailed && pdfSucceeded) {
      let fileName = `${sessionStorage['plant_applicationNo']}_Application.pdf`;
      const blob = new Blob([pdfData], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(cleanPdf());
    }
  }, [pdfData, pdfLoading, pdfFailed, pdfSucceeded]);

  useEffect(() => {
    dispatch(clearForm());
    dispatch(clearPlantItemLicenceTypes());
  }, [dispatch]);

  return (
    <>
      {applicationPaid && (
        <div className={styles.paymentSuccess}>
          <Row>
            <Col span={9}>
              <SectionPage>
                <div className={styles.submittedTitle}>
                  <IconNotificationSuccess />
                  <span>Your application has been submitted.</span>
                </div>
                <div className={styles.confirmationText}>
                  <p>
                    A confirmation email from SafeWork NSW will be sent to
                    {' ' + emailSentTo}.
                  </p>
                  <p>
                    <b>Application number : </b>
                    {refNumber}
                  </p>
                  <p>
                    If you would like a copy of your submitted application for your records, you may download a copy of
                    it now.
                  </p>
                  <p>
                    To align registration dates for all plant items at a single location and qualify for a discounted
                    renewal fee next year, please email{' '}
                    <a href="mailto:licensing@safework.nsw.gov.au">licensing@safework.nsw.gov.au</a> with your plant
                    item details and location.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    fetchSummaryPDF();
                  }}
                >
                  <IconDownload color="secondaryBlue" /> Download
                </Button>
              </SectionPage>
              <div className={styles.nextSteps}>
                <NextSteps items={jsonSteps} title="Next steps" />
              </div>
              <p>
                If you need to discuss your application contact{' '}
                <a href="https://www.safework.nsw.gov.au/">SafeWork NSW</a> on 13 10 50.
              </p>
            </Col>
          </Row>
        </div>
      )}

      <Row>
        <Col span={9}>
          <HorizontalRule />
          <div id="feedbackContainer" />
        </Col>
      </Row>

      {pdfLoading || !applicationPaid ? (
        <Row>
          <Col span={12}>
            <ComponentLoader fullPage />
          </Col>
        </Row>
      ) : null}
    </>
  );
};
